import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fmtDate, initials } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { RtEndeHinweis, RtZipLink, useRtBeendet } from '../../rechnung/KanzleiZip';
import type { ClientDetail } from './types';

// Mandat beenden (Admin, DSGVO): Die Logins des Mandanten sind sofort gesperrt. Nach 3 Monaten löscht die App die
// Unterlagen; Rechnungen und Chat bleiben bis zum Ende ihrer Aufbewahrungsfrist. Bis dahin lässt es sich fortsetzen.

export function MandatBeenden({ detail, reload }: { detail: ClientDetail; reload(): Promise<void> }) {
  const c = detail.client;
  const toast = useToast();
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);

  async function end() {
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/clients/${c.id}/beenden`, {});
      toast(`Mandat mit ${c.name} beendet`);
      await reload();
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
    }
  }

  return (
    <section className="card" aria-labelledby="end-h">
      <div className="ch">
        <h3 id="end-h">Mandat beenden</h3>
      </div>
      <p className="note">
        Wenn die Zusammenarbeit endet: Der Mandant kann sich sofort nicht mehr anmelden. Nach 3 Monaten löscht die App alle
        Unterlagen und Logins. Rechnungen und Chat bleiben bis zum Ende ihrer Aufbewahrungsfrist.
      </p>
      {ask ? (
        <div className="form pk-ask" role="group" aria-labelledby="end-q">
          <b id="end-q">Mandat mit {c.name} beenden?</b>
          <RtEndeHinweis clientId={c.id} />
          <span className="btnrow">
            <button type="button" className="btn sm dark" onClick={() => void end()} disabled={busy}>
              Ja, Mandat beenden
            </button>
            <button type="button" className="btn sm" onClick={() => setAsk(false)} disabled={busy}>
              Abbrechen
            </button>
          </span>
        </div>
      ) : (
        <button type="button" className="btn sm" onClick={() => setAsk(true)}>
          Mandat beenden
        </button>
      )}
    </section>
  );
}

// Hinweis oben in der Mandantenansicht (nur der Admin sieht beendete Mandate)
export function EndedBanner({ detail, reload }: { detail: ClientDetail; reload(): Promise<void> }) {
  const c = detail.client;
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (c.status !== 'ended' || !c.endedAt) return null;

  async function resume() {
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/clients/${c.id}/fortsetzen`, {});
      toast(`Mandat mit ${c.name} fortgesetzt`);
      await reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lock endbar" role="status">
      <span style={{ flex: 1 }}>
        <b>Mandat beendet am {fmtDate(c.endedAt)}.</b>{' '}
        {c.purgedAt
          ? `Die Unterlagen hat die App am ${fmtDate(c.purgedAt)} gelöscht. Rechnungen und Chat bleiben bis zum Ende ihrer Frist.`
          : c.purgeOn
            ? `Der Mandant kann sich nicht mehr anmelden. Am ${fmtDate(c.purgeOn)} löscht die App die Unterlagen und Logins; Rechnungen und Chat bleiben bis zum Ende ihrer Frist.`
            : 'Der Mandant kann sich nicht mehr anmelden. Die Löschsperre ist an: Die App löscht nichts.'}
        {!c.purgedAt && ' Beim Fortsetzen braucht der Mandant ein neues Passwort (Stammdaten: „Passwort zurücksetzen“).'}
      </span>
      {!c.purgedAt && (
        <button type="button" className="btn sm" onClick={() => void resume()} disabled={busy}>
          Mandat fortsetzen
        </button>
      )}
    </div>
  );
}

interface EndedRow {
  id: string;
  number: string | null;
  name: string;
  endedAt: string;
  purgeOn: string | null;
  purgedAt: string | null;
}

// Beendete Mandate (Admin) unter der Mandantenliste, eingeklappt
export function EndedClients() {
  const res = useApi<{ clients: EndedRow[] }>('/api/kanzlei/clients/beendet', { live: ['clients'] });
  const [show, setShow] = useState(false);
  const rows = res.data?.clients ?? [];
  const rt = useRtBeendet(show && rows.length > 0);
  if (!rows.length) return null;
  return (
    <div className="acc rejected">
      <button type="button" className="btn sm" aria-expanded={show} aria-controls="end-list" onClick={() => setShow((s) => !s)}>
        {show ? 'Beendete Mandate ausblenden' : `Beendete Mandate (${rows.length})`}
        <span aria-hidden="true">{show ? ' ▴' : ' ▾'}</span>
      </button>
      {show && (
        <div className="card" id="end-list">
          {rows.map((c) => (
            <div className="task" key={c.id}>
              <span className="av" aria-hidden="true">
                {initials(c.name)}
              </span>
              <div className="tx">
                <b>{c.name}</b>
                <small>
                  {c.number ? `Nr. ${c.number} · ` : ''}beendet am {fmtDate(c.endedAt)} ·{' '}
                  {c.purgedAt
                    ? `Unterlagen gelöscht am ${fmtDate(c.purgedAt)}`
                    : c.purgeOn
                      ? `Löschung am ${fmtDate(c.purgeOn)}`
                      : 'Löschsperre an'}
                </small>
              </div>
              <RtZipLink clientId={c.id} name={c.name} count={rt[c.id]} />
              <Link className="btn sm" to={`/kanzlei/mandanten/${c.id}/stammdaten`} aria-label={`${c.name} öffnen`}>
                Öffnen
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
